import {
  parseHandoffRulesValue,
  serializeHandoffRulesDocument,
} from '@features/Admin/Agent/utils/handoff-rule-mapper';

export type HandoffCanvasLayout = {
  canvasAgentIds: string[];
  nodePositions: Record<string, { x: number; y: number }>;
};

const AUTO_LAYOUT_START_X = 100;

/**
 * Detects layouts produced when auto-layout ran before handoff rules finished
 * hydrating into state (which placed every connected agent into column x=100).
 */
export const isCollapsedSingleColumnLayout = (
  rulesCount: number,
  nodePositions: Record<string, { x: number; y: number }>,
): boolean => {
  if (rulesCount === 0) return false;
  const positions = Object.values(nodePositions);
  if (positions.length < 2) return false;
  return positions.every((pos) => pos.x === AUTO_LAYOUT_START_X);
};

const storageKeyForSquad = (squadId: string) => `handoff-canvas-layout:v2:${squadId}`;

export const loadHandoffCanvasLayout = (squadId: string): HandoffCanvasLayout | null => {
  if (!squadId || typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(storageKeyForSquad(squadId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<HandoffCanvasLayout>;
    const canvasAgentIds = Array.isArray(parsed.canvasAgentIds)
      ? parsed.canvasAgentIds.filter((id): id is string => typeof id === 'string')
      : [];
    const nodePositions =
      parsed.nodePositions && typeof parsed.nodePositions === 'object'
        ? Object.entries(parsed.nodePositions).reduce<Record<string, { x: number; y: number }>>(
            (acc, [agentId, position]) => {
              if (!agentId || !position || typeof position !== 'object') return acc;
              const x = (position as { x?: unknown }).x;
              const y = (position as { y?: unknown }).y;
              if (typeof x !== 'number' || Number.isNaN(x)) return acc;
              if (typeof y !== 'number' || Number.isNaN(y)) return acc;
              acc[agentId] = { x, y };
              return acc;
            },
            {},
          )
        : {};
    if (canvasAgentIds.length === 0 && Object.keys(nodePositions).length === 0) return null;
    return { canvasAgentIds, nodePositions };
  } catch {
    return null;
  }
};

export const saveHandoffCanvasLayout = (squadId: string, layout: HandoffCanvasLayout): void => {
  if (!squadId || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(storageKeyForSquad(squadId), JSON.stringify(layout));
  } catch {
    // Ignore quota / private-mode failures; layout will fall back to auto-layout.
  }
};

/** Merge API handoff rules with the last local canvas layout so refresh keeps the same graph. */
export const withStoredHandoffCanvasLayout = (squadId: string, apiValue: string): string => {
  const api = parseHandoffRulesValue(apiValue);
  if (api.hasError) return apiValue;
  // API / cache already has layout — prefer it.
  if (Object.keys(api.nodePositions).length > 0) return apiValue;

  const stored = loadHandoffCanvasLayout(squadId);
  if (!stored || Object.keys(stored.nodePositions).length === 0) return apiValue;

  const referencedIds = Array.from(
    new Set(
      api.rules
        .flatMap((rule) => [rule.fromAgentId, rule.toAgentId])
        .filter((agentId): agentId is string => Boolean(agentId)),
    ),
  );
  // Only restore positions for agents still referenced by rules — never revive orphans.
  const nodePositions = Object.fromEntries(
    Object.entries(stored.nodePositions).filter(([agentId]) => referencedIds.includes(agentId)),
  );

  if (isCollapsedSingleColumnLayout(api.rules.length, nodePositions)) {
    return apiValue;
  }

  return JSON.stringify(
    serializeHandoffRulesDocument(api.rules, referencedIds, nodePositions),
    null,
    2,
  );
};
